/**
 * Voice AI — Calls Service
 * Owns all call history API calls and formatting helpers.
 */

import { authService } from './auth-service.js';
import { tz } from './timezone-service.js';

const API_BASE = '/voice/php/api';

class CallsService {

  /**
   * Load paginated call list with filters, sorting, and stats.
   * @param {Object} opts
   * @returns {Promise<Object>} { success, calls[], pagination, stats }
   */
  async loadCalls(opts = {}, { signal } = {}) {
    const params = new URLSearchParams();
    if (opts.page) params.set('page', String(opts.page));
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.search) params.set('search', opts.search);
    if (opts.status) params.set('status', opts.status);
    if (opts.direction) params.set('direction', opts.direction);
    if (opts.dateRange && opts.dateRange !== 'all') {
      params.set('date_range', opts.dateRange);
    }
    if (opts.sort) params.set('sort', opts.sort);
    if (opts.sortDir) params.set('sort_dir', opts.sortDir);

    const fetchOpts = {};
    if (signal) fetchOpts.signal = signal;
    const res = await authService.apiFetch(`${API_BASE}/calls.php?${params}`, fetchOpts);
    return res.json();
  }

  /**
   * Load single call detail with transcripts and tool calls.
   * @param {number} callId - Database ID (not the "call-N" string)
   * @returns {Promise<Object>} { success, call, transcripts[], toolCalls[] }
   */
  async getCallDetail(callId) {
    const res = await authService.apiFetch(`${API_BASE}/call-detail.php?id=${callId}`);
    return res.json();
  }

  /**
   * Format duration in seconds to "M:SS" string.
   */
  formatDuration(sec) {
    if (!sec || sec === 0) return '--:--';
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  }

  /**
   * Format ISO date string to readable date.
   */
  formatDate(dateStr) {
    return tz.formatDate(dateStr);
  }

  /**
   * Format ISO date string to readable time.
   */
  formatTime(dateStr) {
    return tz.formatTime(dateStr);
  }

  /**
   * Get user-friendly display label for a DB status value.
   */
  statusDisplayLabel(status) {
    const map = { failed: 'Missed', transferred: 'Transferred' };
    return map[status] || (status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown');
  }

  /**
   * Get CSS class for status badge.
   */
  statusBadgeClass(status) {
    const map = {
      completed: 'badge-success',
      active: 'badge-info',
      missed: 'badge-error',
      abandoned: 'badge-warning',
      transferred: 'badge-warning',
      failed: 'badge-error',
    };
    return map[status] || 'badge-neutral';
  }

  /**
   * Format phone number for display.
   * Converts "+14092953517" to "+1 (409) 295-3517"
   */
  formatPhone(phone) {
    if (!phone) return 'Unknown';
    // Browser/Agent One PTT calls — internal, no real phone number
    if (phone === 'testing' || phone === 'In-App Call') return 'In-App Call';
    const match = phone.match(/^\+?1?(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `+1 (${match[1]}) ${match[2]}-${match[3]}`;
    }
    return phone;
  }

  // ── Notes ──

  async loadNotes(callId) {
    const res = await authService.apiFetch(`${API_BASE}/call-notes.php?call_id=${callId}`);
    return res.json();
  }

  async addNote(callId, content) {
    const res = await authService.apiFetch(`${API_BASE}/call-notes.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ call_id: callId, content })
    });
    return res.json();
  }

  async deleteNote(callId, index) {
    const res = await authService.apiFetch(`${API_BASE}/call-notes.php?call_id=${callId}&index=${index}`, {
      method: 'DELETE'
    });
    return res.json();
  }

  // ── Flags ──

  async addFlag(callId, type, reason = '') {
    const res = await authService.apiFetch(`${API_BASE}/call-flag.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ call_id: callId, type, reason })
    });
    return res.json();
  }

  async removeFlag(callId) {
    const res = await authService.apiFetch(`${API_BASE}/call-flag.php?call_id=${callId}`, {
      method: 'DELETE'
    });
    return res.json();
  }
}

export const callsService = new CallsService();
